import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, CheckCircle2, ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function AuditIssueDialog({ shopId, issueId, open, onOpenChange }) {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["audit-issue", shopId, issueId],
    queryFn: async () =>
      (await api.get(`/shops/${shopId}/audit/issues/${issueId}`)).data,
    enabled: !!shopId && !!issueId && open,
    staleTime: 60 * 1000,
  });

  const copyPrompt = () => {
    if (!data?.emergent_prompt) return;
    navigator.clipboard.writeText(data.emergent_prompt);
    setCopied(true);
    toast.success(t("audit_prompt_copied"));
    setTimeout(() => setCopied(false), 2000);
  };

  const openEmergent = () => {
    window.open("https://app.emergent.sh/", "_blank", "noopener");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 pr-8">
            <span>{data?.title || "…"}</span>
            {data?.severity && (
              <Badge
                variant={data.severity === "critical" ? "destructive" : "secondary"}
                className="font-mono text-[10px] uppercase tracking-widest"
              >
                {data.severity}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs font-mono uppercase tracking-widest">
            {data?.count ? `${data.count} × auf ${data.domain}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto pr-2 flex-1 -mr-2 space-y-5">
          {isLoading || !data ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Problem */}
              <section>
                <h4 className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                  {t("audit_problem")}
                </h4>
                <p className="text-sm leading-relaxed">{data.fix_summary}</p>
              </section>

              {/* Betroffene URLs */}
              {data.sample_urls?.length > 0 && (
                <section>
                  <h4 className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                    {t("audit_affected_urls")}
                  </h4>
                  <ul className="space-y-1">
                    {data.sample_urls.map((u, idx) => (
                      <li
                        key={idx}
                        className="text-xs font-mono text-muted-foreground bg-muted rounded px-3 py-1.5"
                      >
                        {u}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[10px] text-muted-foreground mt-2 italic">
                    {t("audit_urls_hint").replace("{n}", data.count)}
                  </p>
                </section>
              )}

              {/* Fix-Anleitung */}
              {data.how_to_fix?.length > 0 && (
                <section>
                  <h4 className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                    {t("audit_how_to_fix")}
                  </h4>
                  <ol className="space-y-2">
                    {data.how_to_fix.map((step, idx) => (
                      <li key={idx} className="text-sm flex gap-3">
                        <span className="font-mono text-[10px] mt-0.5 shrink-0 h-5 w-5 rounded-full bg-foreground text-background grid place-items-center">
                          {idx + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {/* Emergent-Prompt */}
              <section className="rounded-lg border border-[hsl(var(--chart-1))]/30 bg-[hsl(var(--chart-1))]/[0.05] overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[hsl(var(--chart-1))]/20 bg-[hsl(var(--chart-1))]/[0.08]">
                  <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--chart-1))]" />
                  <span className="text-xs font-mono uppercase tracking-widest font-semibold">
                    {t("audit_emergent_prompt")}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyPrompt}
                    data-testid="audit-prompt-copy"
                    className="ml-auto gap-1.5 h-7"
                  >
                    {copied ? (
                      <>
                        <CheckCircle2 className="h-3 w-3 text-[hsl(var(--chart-3))]" />
                        {t("copied")}
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        {t("copy")}
                      </>
                    )}
                  </Button>
                </div>
                <pre className="text-[11px] leading-relaxed font-mono bg-background/50 p-4 overflow-x-auto whitespace-pre-wrap max-h-72 overflow-y-auto">
                  {data.emergent_prompt}
                </pre>
                <div className="px-4 py-3 border-t border-[hsl(var(--chart-1))]/20 bg-[hsl(var(--chart-1))]/[0.03] flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-muted-foreground">
                    {t("audit_emergent_hint")}
                  </p>
                  <Button
                    size="sm"
                    onClick={openEmergent}
                    data-testid="audit-open-emergent"
                    className="gap-1.5 shrink-0"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {t("audit_open_emergent")}
                  </Button>
                </div>
              </section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
